import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarX2,
  CalendarSearch,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Loader2,
  Send,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { TripResponse, historyApi, tripsApi } from '../lib/api';
import { logger } from '../lib/logger';
import { formatDuration } from '../lib/utils';
import { formatDateLong, formatDateShort, getDirectionLabel, getRouteName, normalizeTime } from '../lib/tripPresentation';

const getMonthGroupLabel = (travelDate: string): string => {
  const date = new Date(`${travelDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return travelDate;
  const month = date.toLocaleString('es-CL', { month: 'long' });
  const year = date.getFullYear();
  return `${month.charAt(0).toUpperCase()}${month.slice(1).toLowerCase()} de ${year}`;
};

interface TripHistoryPageProps {
  onRequireAuth: () => void;
}

type PeriodFilter = 'this-month' | 'previous-month' | 'three-months' | 'month-year';
type RouteFilter = 'all' | '68' | '78';
type DirectionFilter = 'all' | 'santiago-litoral' | 'litoral-santiago';
type SortFilter = 'recent' | 'oldest';

interface SummaryState {
  total: number;
  route68: number;
  route78: number;
  outbound: number;
  inbound: number;
  loading: boolean;
}

const toYmd = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toMonthValue = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const clampMonthToCurrent = (value: string): string => {
  if (!/^\d{4}-\d{2}$/.test(value)) {
    return toMonthValue(new Date());
  }
  const now = new Date();
  const [year, month] = value.split('-').map(Number);
  const selected = year * 100 + month;
  const current = now.getFullYear() * 100 + (now.getMonth() + 1);
  return selected > current ? toMonthValue(now) : value;
};

const getPeriodRange = (period: PeriodFilter, customMonth: string): { fromDate: string; toDate: string } => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  if (period === 'month-year' && customMonth) {
    const safeMonth = clampMonthToCurrent(customMonth);
    const [customYear, customMonthValue] = safeMonth.split('-').map(Number);
    const start = new Date(customYear, customMonthValue - 1, 1);
    const end = new Date(customYear, customMonthValue, 0);
    return { fromDate: toYmd(start), toDate: toYmd(end) };
  }

  if (period === 'previous-month') {
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0);
    return { fromDate: toYmd(start), toDate: toYmd(end) };
  }

  if (period === 'three-months') {
    const start = new Date(year, month - 2, 1);
    const end = new Date(year, month + 1, 0);
    return { fromDate: toYmd(start), toDate: toYmd(end) };
  }

  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  return { fromDate: toYmd(start), toDate: toYmd(end) };
};

const trafficLabel = (level: string | null): string => {
  if (!level) return 'Sin datos';
  if (level === 'LIGHT') return 'Fluido';
  if (level === 'MODERATE') return 'Moderado';
  if (level === 'HEAVY') return 'Alto';
  if (level === 'SEVERE') return 'Congestionado';
  return 'Sin datos';
};

export const TripHistoryPage: React.FC<TripHistoryPageProps> = ({ onRequireAuth }) => {
  const { user } = useAuth();
  const [period, setPeriod] = useState<PeriodFilter>('this-month');
  const [customMonth, setCustomMonth] = useState(toMonthValue(new Date()));
  const [routeFilter, setRouteFilter] = useState<RouteFilter>('all');
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>('all');
  const [sortFilter, setSortFilter] = useState<SortFilter>('recent');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [items, setItems] = useState<TripResponse[]>([]);
  const [summary, setSummary] = useState<SummaryState>({
    total: 0,
    route68: 0,
    route78: 0,
    outbound: 0,
    inbound: 0,
    loading: false,
  });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [detailTripId, setDetailTripId] = useState<string | null>(null);

  const [didTrip, setDidTrip] = useState<boolean | null>(null);
  const [estimateAccurate, setEstimateAccurate] = useState<boolean | null>(null);
  const [feedbackSaved, setFeedbackSaved] = useState(false);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const summaryRequestRef = useRef(0);

  const periodRange = useMemo(() => getPeriodRange(period, customMonth), [period, customMonth]);

  const visibleItems = useMemo(() => {
    if (directionFilter === 'all') {
      return items;
    }
    return items.filter((item) => item.direction === directionFilter);
  }, [items, directionFilter]);

  const selectedTrip = useMemo(
    () => visibleItems.find((item) => item.id === selectedTripId) ?? null,
    [visibleItems, selectedTripId]
  );

  const detailTrip = useMemo(
    () => items.find((item) => item.id === detailTripId) ?? null,
    [items, detailTripId]
  );

  const sortParam = sortFilter === 'recent' ? 'travel_date_desc' : 'travel_date_asc';

  const hasActiveFilters = routeFilter !== 'all' || directionFilter !== 'all' || sortFilter !== 'recent';

  const groupedItems = useMemo(() => {
    const groups: Array<{ label: string; items: TripResponse[] }> = [];
    const seen = new Map<string, TripResponse[]>();
    for (const item of visibleItems) {
      const label = getMonthGroupLabel(item.travelDate);
      if (!seen.has(label)) {
        seen.set(label, []);
        groups.push({ label, items: seen.get(label)! });
      }
      seen.get(label)!.push(item);
    }
    return groups;
  }, [visibleItems]);

  const loadHistory = useCallback(
    async (append = false, currentOffset = 0) => {
      if (!user) return;
      append ? setLoadingMore(true) : setLoading(true);

      try {
        const response = await tripsApi.list({
          type: 'past',
          sort: sortParam,
          limit: 20,
          offset: currentOffset,
          fromDate: periodRange.fromDate,
          toDate: periodRange.toDate,
          routeId: routeFilter === 'all' ? undefined : routeFilter,
        });

        setItems((previous) => (append ? [...previous, ...response.items] : response.items));
        setOffset(currentOffset + response.items.length);
        setHasMore(response.hasMore);
      } catch (error) {
        logger.error(
          'No se pudo cargar historial de viajes',
          error instanceof Error ? error : new Error(String(error)),
          { period, routeFilter, sortFilter, ...periodRange }
        );
      } finally {
        append ? setLoadingMore(false) : setLoading(false);
      }
    },
    [period, periodRange, routeFilter, sortFilter, sortParam, user]
  );

  useEffect(() => {
    setOffset(0);
    setHasMore(false);
    setItems([]);
    setSelectedTripId(null);
    setDetailTripId(null);
    setDidTrip(null);
    setEstimateAccurate(null);
    setFeedbackSaved(false);
    void loadHistory(false, 0);
  }, [loadHistory]);

  useEffect(() => {
    if (visibleItems.length === 0) {
      setSelectedTripId(null);
      return;
    }

    if (!selectedTripId || !visibleItems.some((item) => item.id === selectedTripId)) {
      setSelectedTripId(visibleItems[0].id);
    }
  }, [visibleItems, selectedTripId]);

  useEffect(() => {
    if (!detailTrip || !user) return;

    const loadFeedback = async () => {
      setLoadingFeedback(true);
      try {
        const response = await historyApi.getTripFeedback(detailTrip.id);
        if (response.didTrip === null || response.didTrip === undefined) {
          setDidTrip(null);
          setEstimateAccurate(null);
          setFeedbackSaved(false);
        } else {
          setDidTrip(response.didTrip);
          setEstimateAccurate(response.estimateAccurate ?? null);
          setFeedbackSaved(true);
        }
      } catch (error) {
        logger.error(
          'No se pudo cargar feedback de viaje',
          error instanceof Error ? error : new Error(String(error)),
          { tripId: detailTrip.id }
        );
        setDidTrip(null);
        setEstimateAccurate(null);
        setFeedbackSaved(false);
      } finally {
        setLoadingFeedback(false);
      }
    };

    void loadFeedback();
  }, [detailTrip, user]);

  const computeSummary = useCallback(
    (allItems: TripResponse[]): Omit<SummaryState, 'loading'> => {
      const filteredItems =
        directionFilter === 'all'
          ? allItems
          : allItems.filter((item) => item.direction === directionFilter);

      return {
        total: filteredItems.length,
        route68: filteredItems.filter((item) => item.routeId === '68').length,
        route78: filteredItems.filter((item) => item.routeId === '78').length,
        outbound: filteredItems.filter((item) => item.direction === 'santiago-litoral').length,
        inbound: filteredItems.filter((item) => item.direction === 'litoral-santiago').length,
      };
    },
    [directionFilter]
  );

  const loadSummaryFallback = useCallback(async () => {
    const limit = 100;
    let currentOffset = 0;
    const allItems: TripResponse[] = [];

    while (true) {
      const response = await tripsApi.list({
        type: 'past',
        sort: 'travel_date_desc',
        limit,
        offset: currentOffset,
        fromDate: periodRange.fromDate,
        toDate: periodRange.toDate,
        routeId: routeFilter === 'all' ? undefined : routeFilter,
      });

      allItems.push(...response.items);
      currentOffset += response.items.length;

      if (!response.hasMore || response.items.length === 0) {
        break;
      }
    }

    return computeSummary(allItems);
  }, [computeSummary, periodRange, routeFilter]);

  const loadSummary = useCallback(async () => {
    if (!user) return;

    const requestId = summaryRequestRef.current + 1;
    summaryRequestRef.current = requestId;
    setSummary((previous) => ({ ...previous, loading: true }));

    try {
      const response = await tripsApi.summary({
        type: 'past',
        routeId: routeFilter === 'all' ? undefined : routeFilter,
        direction: directionFilter === 'all' ? undefined : directionFilter,
        fromDate: periodRange.fromDate,
        toDate: periodRange.toDate,
      });

      if (summaryRequestRef.current !== requestId) return;

      setSummary({
        total: response.total,
        route68: response.byRoute.route68,
        route78: response.byRoute.route78,
        outbound: response.byDirection.santiagoLitoral,
        inbound: response.byDirection.litoralSantiago,
        loading: false,
      });
    } catch (error) {
      if (summaryRequestRef.current !== requestId) return;

      try {
        const fallbackSummary = await loadSummaryFallback();
        if (summaryRequestRef.current !== requestId) return;
        setSummary({
          ...fallbackSummary,
          loading: false,
        });
        logger.warn('Resumen de viajes por API no disponible, aplicado fallback paginado', {
          period,
          routeFilter,
          directionFilter,
          ...periodRange,
        });
      } catch (fallbackError) {
        if (summaryRequestRef.current !== requestId) return;
        setSummary((previous) => ({ ...previous, loading: false }));
        logger.error(
          'No se pudo calcular resumen completo de viajes',
          fallbackError instanceof Error ? fallbackError : new Error(String(fallbackError)),
          { period, routeFilter, directionFilter, ...periodRange }
        );
      }
    }
  }, [directionFilter, loadSummaryFallback, period, periodRange, routeFilter, user]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const submitFeedback = async () => {
    if (!detailTrip || didTrip === null) return;
    setSubmittingFeedback(true);
    try {
      await historyApi.saveTripFeedback(detailTrip.id, {
        didTrip,
        estimateAccurate: didTrip ? estimateAccurate ?? undefined : undefined,
      });
      setFeedbackSaved(true);
    } catch (error) {
      logger.error(
        'No se pudo guardar feedback del viaje',
        error instanceof Error ? error : new Error(String(error)),
        { tripId: detailTrip.id }
      );
    } finally {
      setSubmittingFeedback(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="bg-white border border-gray-200 rounded-3xl p-12 text-center">
          <CalendarSearch className="mx-auto text-gray-400 mb-4" size={42} />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Mis viajes</h2>
          <p className="text-gray-500 mb-8">Inicia sesión para revisar tu historial de viajes.</p>
          <button
            onClick={onRequireAuth}
            className="px-8 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors"
          >
            Iniciar sesión
          </button>
        </div>
      </div>
    );
  }

  if (detailTrip) {
    return (
      <div className="max-w-5xl mx-auto px-6 pb-16 animate-fade-in">
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => setDetailTripId(null)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft size={15} />
            Volver
          </button>
          <div>
            <h2 className="text-2xl font-black text-gray-900">{getDirectionLabel(detailTrip.direction)}</h2>
            <p className="text-sm text-gray-500">{formatDateLong(detailTrip.travelDate)}</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6">
          <div className="divide-y divide-gray-100 text-sm">
            <div className="flex justify-between py-3 first:pt-0">
              <span className="text-gray-500">Ruta</span>
              <span className="font-semibold">{getRouteName(detailTrip.routeId)}</span>
            </div>
            <div className="flex justify-between py-3">
              <span className="text-gray-500">Sentido</span>
              <span className="font-semibold">{getDirectionLabel(detailTrip.direction)}</span>
            </div>
            <div className="flex justify-between py-3">
              <span className="text-gray-500">Fecha</span>
              <span className="font-semibold capitalize">{formatDateLong(detailTrip.travelDate)}</span>
            </div>
            <div className="flex justify-between py-3">
              <span className="text-gray-500">Hora ingreso</span>
              <span className="font-semibold">{normalizeTime(detailTrip.scheduledTime)}h</span>
            </div>
            <div className="flex justify-between py-3">
              <span className="text-gray-500">Duración estimada</span>
              <span className="font-semibold">
                {detailTrip.estimatedDurationMinutes ? formatDuration(detailTrip.estimatedDurationMinutes) : '—'}
              </span>
            </div>
            <div className="flex justify-between py-3 last:pb-0">
              <span className="text-gray-500">Tráfico proyectado</span>
              <span className="font-semibold">{trafficLabel(detailTrip.trafficLevel)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          {loadingFeedback ? (
            <div className="text-center text-gray-500 py-8">
              <Loader2 size={24} className="animate-spin mx-auto mb-2" />
              Cargando estado del viaje...
            </div>
          ) : feedbackSaved ? (
            <div className="flex items-center gap-2 text-emerald-700 text-sm font-medium py-2">
              <Check size={16} className="text-emerald-500" />
              Feedback ya registrado para este viaje.
            </div>
          ) : (
            <>
              <p className="font-semibold text-gray-900 mb-3">¿Realizaste este viaje?</p>
              <div className="space-y-2 mb-4">
                <label className="flex items-center gap-2 text-gray-700">
                  <input
                    type="radio"
                    checked={didTrip === true}
                    onChange={() => {
                      setDidTrip(true);
                      setFeedbackSaved(false);
                    }}
                  />
                  Sí, lo realicé
                </label>
                <label className="flex items-center gap-2 text-gray-700">
                  <input
                    type="radio"
                    checked={didTrip === false}
                    onChange={() => {
                      setDidTrip(false);
                      setEstimateAccurate(null);
                      setFeedbackSaved(false);
                    }}
                  />
                  No lo realicé
                </label>
              </div>

              {didTrip === true && (
                <>
                  <p className="font-semibold text-gray-900 mb-2">¿La estimación fue precisa?</p>
                  <div className="space-y-2 mb-4">
                    <label className="flex items-center gap-2 text-gray-700">
                      <input
                        type="radio"
                        checked={estimateAccurate === true}
                        onChange={() => {
                          setEstimateAccurate(true);
                          setFeedbackSaved(false);
                        }}
                      />
                      Sí, fue precisa
                    </label>
                    <label className="flex items-center gap-2 text-gray-700">
                      <input
                        type="radio"
                        checked={estimateAccurate === false}
                        onChange={() => {
                          setEstimateAccurate(false);
                          setFeedbackSaved(false);
                        }}
                      />
                      No, fue diferente
                    </label>
                  </div>
                </>
              )}

              <button
                onClick={() => void submitFeedback()}
                disabled={submittingFeedback || didTrip === null || (didTrip === true && estimateAccurate === null)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50"
              >
                {submittingFeedback ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Send size={15} />
                    Enviar feedback
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="max-w-6xl mx-auto px-6 pb-16 animate-fade-in">
      <div className="mb-8">
        <h1 className="text-4xl font-black text-gray-900 mb-2">Mis viajes</h1>
        <p className="text-gray-500">Historial de viajes realizados</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Periodo</p>
          <div className="flex flex-wrap items-center gap-2">
              {([
                { value: 'this-month', label: 'Este mes' },
                { value: 'previous-month', label: 'Mes anterior' },
                { value: 'three-months', label: '3 meses' },
                { value: 'month-year', label: 'Mes/Año' },
              ] as const).map((item) => (
                <button
                  key={item.value}
                  onClick={() => { setPeriod(item.value); setIsFilterModalOpen(false); }}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    period === item.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              {period === 'month-year' && (
                <input
                  type="month"
                  value={customMonth}
                  max={toMonthValue(new Date())}
                  onChange={(event) => setCustomMonth(clampMonthToCurrent(event.target.value))}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white"
                />
              )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-500 mb-1">Este periodo</p>
          <p className="text-4xl font-black text-blue-600">{summary.loading ? '—' : summary.total}</p>
          <p className="text-sm text-gray-500 mt-1">viajes registrados</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-500 mb-2">Por ruta</p>
          <div className="flex items-center justify-between text-lg font-semibold text-gray-700">
            <span>Ruta 68</span>
            <span>{summary.loading ? '—' : summary.route68}</span>
          </div>
          <div className="flex items-center justify-between text-lg font-semibold text-gray-700">
            <span>Ruta 78</span>
            <span>{summary.loading ? '—' : summary.route78}</span>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-500 mb-2">Por sentido</p>
          <div className="flex items-center justify-between text-lg font-semibold text-gray-700">
            <span>Stgo → Litoral</span>
            <span>{summary.loading ? '—' : summary.outbound}</span>
          </div>
          <div className="flex items-center justify-between text-lg font-semibold text-gray-700">
            <span>Litoral → Stgo</span>
            <span>{summary.loading ? '—' : summary.inbound}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-3xl font-black text-gray-900">Viajes pasados</h2>
            <button
              onClick={() => setIsFilterModalOpen(true)}
              className={`relative inline-flex items-center justify-center w-9 h-9 rounded-xl border transition-colors ${hasActiveFilters ? 'border-blue-300 text-blue-600' : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              aria-label="Filtrar y ordenar"
            >
              <SlidersHorizontal size={16} />
              {hasActiveFilters && (
                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-blue-600" />
              )}
            </button>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[...Array.from({ length: 4 })].map((_, idx) => (
                <div key={idx} className="h-28 rounded-2xl bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
              <CalendarX2 className="mx-auto text-gray-400 mb-3" size={34} />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Sin viajes en este periodo</h3>
              <p className="text-gray-500">No encontramos registros para los filtros seleccionados.</p>
            </div>
          ) : (
            <>
              <div className="space-y-5">
                {groupedItems.map((group) => (
                  <div key={group.label}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2 px-1 capitalize">
                      {group.label}
                    </p>
                    <div className="space-y-3">
                      {group.items.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => setSelectedTripId(item.id)}
                          className={`w-full text-left bg-white border rounded-2xl p-4 transition-colors ${
                            selectedTripId === item.id ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold ${item.routeId === '78' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                                R{item.routeId}
                              </span>
                              <span className="font-semibold text-gray-900 text-base truncate">{getDirectionLabel(item.direction)}</span>
                            </div>
                            <ChevronRight className="shrink-0 text-gray-400" size={18} />
                          </div>
                          <p className="text-gray-500 mt-1.5 flex items-center gap-2 flex-wrap text-sm">
                            <span>{formatDateShort(item.travelDate)}</span>
                            <span>·</span>
                            <span>{normalizeTime(item.scheduledTime)}h</span>
                            <span>·</span>
                            <span className="text-xs font-semibold text-blue-600">Calificar →</span>
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {hasMore && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => void loadHistory(true, offset)}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                    disabled={loadingMore}
                  >
                    {loadingMore ? 'Cargando...' : 'Cargar más'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5 h-fit sticky top-28">
          {!selectedTrip ? (
            <div className="text-center py-12 text-gray-400">
              <Clock3 size={36} className="mx-auto mb-2" />
              <p>Selecciona un viaje para ver el resumen.</p>
            </div>
          ) : (
            <>
              <h3 className="text-2xl font-black text-gray-900 mb-1">Resumen del viaje</h3>
              <p className="text-gray-500 mb-4">{getDirectionLabel(selectedTrip.direction)}</p>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Ruta</span>
                  <span className="font-semibold">{getRouteName(selectedTrip.routeId)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Fecha</span>
                  <span className="font-semibold capitalize">{formatDateLong(selectedTrip.travelDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Hora ingreso</span>
                  <span className="font-semibold">{normalizeTime(selectedTrip.scheduledTime)}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Duración estimada</span>
                  <span className="font-semibold">
                    {selectedTrip.estimatedDurationMinutes ? formatDuration(selectedTrip.estimatedDurationMinutes) : '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Tráfico proyectado</span>
                  <span className="font-semibold">{trafficLabel(selectedTrip.trafficLevel)}</span>
                </div>
              </div>

              <button
                onClick={() => setDetailTripId(selectedTrip.id)}
                className="w-full mt-4 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-blue-600 text-blue-600 font-semibold hover:bg-blue-50 text-sm transition-colors"
              >
                Ver detalle completo <ChevronRight size={14} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>

    {isFilterModalOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
          onClick={() => setIsFilterModalOpen(false)}
        />
        <div className="relative z-10 w-full max-w-2xl bg-white border border-gray-200 rounded-3xl shadow-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-2xl font-black text-gray-900">Filtrar y ordenar</h2>
            <button
              onClick={() => setIsFilterModalOpen(false)}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
              aria-label="Cerrar filtros"
            >
              <X size={18} />
            </button>
          </div>
          <div className="space-y-6">
            <div>
              <p className="text-sm font-semibold text-gray-500 mb-2">Ordenar</p>
              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                {([
                  { value: 'recent', label: 'Más reciente primero' },
                  { value: 'oldest', label: 'Más antiguo primero' },
                ] as const).map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSortFilter(option.value)}
                    className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 text-gray-700"
                  >
                    <span className={sortFilter === option.value ? 'text-blue-600 font-semibold' : ''}>{option.label}</span>
                    {sortFilter === option.value && <Check size={18} className="text-blue-600" />}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 mb-2">Ruta</p>
              <div className="flex flex-wrap items-center gap-2 p-3 rounded-2xl bg-gray-50">
                {([
                  { value: 'all', label: 'Todas' },
                  { value: '68', label: 'Ruta 68' },
                  { value: '78', label: 'Ruta 78' },
                ] as const).map((item) => (
                  <button
                    key={item.value}
                    onClick={() => setRouteFilter(item.value)}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${routeFilter === item.value ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 mb-2">Dirección</p>
              <div className="flex flex-wrap items-center gap-2 p-3 rounded-2xl bg-gray-50">
                {([
                  { value: 'all', label: 'Todos' },
                  { value: 'santiago-litoral', label: 'Stgo → Litoral' },
                  { value: 'litoral-santiago', label: 'Litoral → Stgo' },
                ] as const).map((item) => (
                  <button
                    key={item.value}
                    onClick={() => setDirectionFilter(item.value)}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${directionFilter === item.value ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setSortFilter('recent'); setRouteFilter('all'); setDirectionFilter('all'); }}
                className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-semibold hover:bg-gray-50"
              >
                Restablecer
              </button>
              <button
                onClick={() => setIsFilterModalOpen(false)}
                className="px-5 py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700"
              >
                Listo
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};
